import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin } from 'lucide-react';
import { leadApi, taskApi } from '../../services/api';
import { Lead, Task } from '../../types';
import { fmtAgo, fmtDayTime, isLate, label } from '../../lib';
import { useCallWindows } from '../../context/CallWindowsContext';
import { StageChip } from '../WorkBits';
import { CallIcon } from '../BrandIcons';
import CallModal from '../CallModal';
import SiteVisitModal from '../SiteVisitModal';

/** A lead at a glance — the facts an operator needs mid-conversation, plus the
 *  three moves (call, book the visit, open the full page). Shown beside a chat
 *  and in the dashboard's dot drawer; the Nurture app's LeadGlance. */
const LeadGlance: React.FC<{ leadId: string }> = ({ leadId }) => {
  const navigate = useNavigate();
  const windows = useCallWindows();
  const [lead, setLead] = useState<Lead | null>(null);
  const [visit, setVisit] = useState<Task | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [calling, setCalling] = useState(false);
  const [booking, setBooking] = useState(false);

  const load = useCallback(async () => {
    try {
      const [l, t] = await Promise.all([leadApi.get(leadId), taskApi.listForLead(leadId, 'open')]);
      setLead(l);
      setVisit(t.tasks.find((x) => x.type === 'site_visit') || null);
      setError(null);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err.message);
    }
  }, [leadId]);

  useEffect(() => { setLead(null); load(); }, [load]);

  if (error) return <p className="text-brick text-[13px]">Couldn't load the lead: {error}</p>;
  if (!lead) return <p className="text-graphite text-[13px]">Loading the lead…</p>;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2 flex-wrap">
        <b className="font-display text-[17px]">{lead.name}</b>
        <StageChip stage={lead.stage} />
      </div>
      <div>
        <div className="kv"><span>Phone</span><span className="num">{lead.mobile || '—'}</span></div>
        {lead.acquisition_label && <div className="kv"><span>Came from</span><span>{lead.acquisition_label}</span></div>}
        {lead.location && <div className="kv"><span>Location</span><span>{lead.location}</span></div>}
        <div className="kv">
          <span>Next</span>
          <span className={lead.next_action_at && isLate(lead.next_action_at, windows) ? 'text-brick' : ''}>
            {lead.next_action_at ? `${label(lead.next_action_type || 'action')} · ${fmtDayTime(lead.next_action_at)}` : '—'}
          </span>
        </div>
        <div className="kv">
          <span>Last</span>
          <span>{lead.last_activity_at ? `${label(lead.last_activity_type || 'activity')} · ${fmtAgo(lead.last_activity_at)}` : 'No activity'}</span>
        </div>
        {visit && <div className="kv"><span>Visit</span><span className="text-forest">{fmtDayTime(visit.due_at)}</span></div>}
      </div>
      <div className="flex gap-1.5 flex-wrap">
        <button className="icb" title="Call" disabled={!lead.mobile} onClick={() => setCalling(true)}>
          <CallIcon className="w-[18px] h-[18px]" />
        </button>
        <button className="btn btn-sm flex items-center gap-1.5" onClick={() => setBooking(true)}>
          <MapPin className="w-3.5 h-3.5 text-gold" strokeWidth={1.9} />{visit ? 'Open visit' : 'Book visit'}
        </button>
        <button className="btn btn-sm btn-pri" onClick={() => navigate(`/leads/${lead.id}`)}>Open lead page</button>
      </div>

      {calling && <CallModal lead={lead} onClose={() => setCalling(false)} />}
      {booking && (
        <SiteVisitModal lead={lead} visitTask={visit} onClose={() => setBooking(false)} onChanged={load} />
      )}
    </div>
  );
};

export default LeadGlance;
